const ease = t => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2;
const lerp = (a, b, t) => a + (b - a) * t;

class TestimonialCarouselComponent extends HTMLElement {
  connectedCallback() {
    this.loop = this.dataset.loop !== 'false';
    this.track = this.querySelector('.testimonial-carousel__track');
    if (!this.track || this.track.children.length < 1) return;

    this.quotePanel = this.querySelector('.testimonial-carousel__quote-panel');
    this.prevBtn = this.querySelector('.testimonial-carousel__btn--prev');
    this.nextBtn = this.querySelector('.testimonial-carousel__btn--next');
    this.N = this.track.children.length;
    this.activeIdx = 0;
    this.busy = false;

    this.initQuotePanel();
    this.initLayout();
    this.updateVideoStates();

    this.prevBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));

    this.track.addEventListener('click', e => {
      const btn = e.target.closest('.testimonial-carousel__play-btn');
      if (btn) {
        e.stopPropagation();
        const slide = btn.closest('.testimonial-carousel__slide');
        const video = slide?.querySelector('.testimonial-carousel__video');
        if (!video) return;
        if (video.paused) {
          video.play().catch(() => {});
          this.setPlayBtnState(btn, true);
        } else {
          video.pause();
          this.setPlayBtnState(btn, false);
        }
        return;
      }

      const slide = e.target.closest('.testimonial-carousel__slide');
      if (!slide || slide === this.track.children[0]) return;
      this.go(1);
    });

    document.addEventListener('keydown', e => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const { top, bottom } = this.getBoundingClientRect();
      if (top >= window.innerHeight || bottom <= 0) return;
      this.go(e.key === 'ArrowRight' ? 1 : -1);
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.initLayout(), 150);
    });
  }

  getDims(el) {
    const mobile = window.innerWidth < 990;
    const s = getComputedStyle(el);
    const AW = parseFloat(s.getPropertyValue(mobile ? '--tc-img-w-mobile' : '--tc-img-w')) || (mobile ? 200 : 245);
    const AH = parseFloat(s.getPropertyValue(mobile ? '--tc-img-h-mobile' : '--tc-img-h')) || (mobile ? 275 : 335);
    return { AW, AH, IW: AW * 0.75, IH: AH * 0.75 };
  }

  applyActive(el, active) {
    const { AW, AH, IW, IH } = this.getDims(el);
    el.style.width = (active ? AW : IW) + 'px';
    const img = el.querySelector('.testimonial-carousel__image-slide');
    if (img) img.style.height = (active ? AH : IH) + 'px';
  }

  initLayout() {
    this.GAP = window.innerWidth < 990 ? 18 : 24;
    [...this.track.children].forEach((el, i) => {
      el.style.marginRight = this.GAP + 'px';
      this.applyActive(el, i === 0);
    });
    this.track.style.transform = 'translate3d(0,0,0)';
    const maxAH = Math.max(...[...this.track.children].map(el => this.getDims(el).AH));
    this.track.style.minHeight = maxAH + 'px';
  }

  initQuotePanel() {
    if (!this.quotePanel) return;
    this.quotePanel.innerHTML = '';
    this.quoteItems = [...this.track.children].map((slide, i) => {
      const src = slide.querySelector('.testimonial-carousel__slide-quote');
      const item = document.createElement('div');
      item.className = 'testimonial-carousel__quote-item' + (i === 0 ? ' testimonial-carousel__quote-item--active' : '');
      if (src) item.innerHTML = src.innerHTML;
      this.quotePanel.appendChild(item);
      return item;
    });
  }

  updateActiveQuote() {
    this.quoteItems?.forEach((item, i) =>
      item.classList.toggle('testimonial-carousel__quote-item--active', i === this.activeIdx)
    );
  }

  setPlayBtnState(btn, playing) {
    const play = btn.querySelector('.svg-wrapper--play');
    const pause = btn.querySelector('.svg-wrapper--pause');
    if (play) play.hidden = playing;
    if (pause) pause.hidden = !playing;
    btn.setAttribute('aria-label', playing
      ? btn.getAttribute('data-pause-label') || 'Pause video'
      : btn.getAttribute('data-play-label') || 'Play video'
    );
    btn.dataset.playing = playing ? 'true' : 'false';
  }

  updateVideoStates() {
    [...this.track.children].forEach((slide, i) => {
      const video = slide.querySelector('.testimonial-carousel__video');
      if (!video) return;
      const btn = slide.querySelector('.testimonial-carousel__play-btn');
      if (i === 0) {
        video.play().catch(() => {});
        if (btn) this.setPlayBtnState(btn, true);
      } else {
        video.pause();
        if (btn) this.setPlayBtnState(btn, false);
      }
    });
  }

  go(dir) {
    if (this.busy || this.N < 2) return;
    this.busy = true;

    const DUR = 580;
    const els = [...this.track.children];

    if (dir === 1) {
      const from = els[0], to = els[1];
      const fd = this.getDims(from), td = this.getDims(to);
      const fi = from.querySelector('.testimonial-carousel__image-slide');
      const ti = to.querySelector('.testimonial-carousel__image-slide');
      const t0 = performance.now();

      const step = now => {
        const raw = Math.min((now - t0) / DUR, 1);
        const et = ease(raw);

        from.style.width = lerp(fd.AW, fd.IW, et) + 'px';
        if (fi) fi.style.height = lerp(fd.AH, fd.IH, et) + 'px';

        to.style.width = lerp(td.IW, td.AW, et) + 'px';
        if (ti) ti.style.height = lerp(td.IH, td.AH, et) + 'px';

        this.track.style.transform = `translate3d(${-(fd.IW + this.GAP) * et}px,0,0)`;

        if (raw < 1) { requestAnimationFrame(step); return; }

        this.applyActive(from, false);
        this.applyActive(to, true);
        this.track.appendChild(from);
        this.track.style.transform = 'translate3d(0,0,0)';
        this.activeIdx = (this.activeIdx + 1) % this.N;
        this.updateActiveQuote();
        this.updateVideoStates();
        requestAnimationFrame(() => { this.busy = false; });
      };
      requestAnimationFrame(step);

    } else {
      const last = els[els.length - 1], from = els[0];
      const ld = this.getDims(last), fd = this.getDims(from);
      const li = last.querySelector('.testimonial-carousel__image-slide');
      const fi = from.querySelector('.testimonial-carousel__image-slide');

      this.applyActive(last, false);
      this.track.insertBefore(last, from);
      this.track.style.transform = `translate3d(${-(ld.IW + this.GAP)}px,0,0)`;

      const t0 = performance.now();

      const step = now => {
        const raw = Math.min((now - t0) / DUR, 1);
        const et = ease(raw);

        last.style.width = lerp(ld.IW, ld.AW, et) + 'px';
        if (li) li.style.height = lerp(ld.IH, ld.AH, et) + 'px';

        from.style.width = lerp(fd.AW, fd.IW, et) + 'px';
        if (fi) fi.style.height = lerp(fd.AH, fd.IH, et) + 'px';

        this.track.style.transform = `translate3d(${-(ld.IW + this.GAP) * (1 - et)}px,0,0)`;

        if (raw < 1) { requestAnimationFrame(step); return; }

        this.applyActive(last, true);
        this.applyActive(from, false);
        this.track.style.transform = 'translate3d(0,0,0)';
        this.activeIdx = (this.activeIdx - 1 + this.N) % this.N;
        this.updateActiveQuote();
        this.updateVideoStates();
        requestAnimationFrame(() => { this.busy = false; });
      };
      requestAnimationFrame(step);
    }
  }
}

customElements.define('testimonial-carousel-component', TestimonialCarouselComponent);
